// Pure helpers for the moderation rule editor. resolveScenario mirrors
// Admin::ModerationDecision exactly, so the editor's interactive preview can't
// misrepresent what a rule set does. The engine, for content of type T, applies the
// most-severe ENABLED rule whose report count meets its threshold — where an
// "any reason" rule counts EVERY unreviewed report on the target and a reason-specific
// rule counts only that reason's. Because those two counts move independently, there
// is no fixed ordering of which rule "wins"; it depends on the actual counts. So the
// preview evaluates a concrete scenario (this reason's reports + other reasons')
// rather than claiming a static ladder.

export const ACTION_SEVERITY = { log_only: 0, queue_for_review: 1, hide_for_review: 2 };

export const WILDCARD_REASON_LABEL = "All reasons (net)";
export const WILDCARD_TYPE_LABEL = "All content types";

export function reasonLabel(reason, labels = {}) {
  if (!reason) return WILDCARD_REASON_LABEL;
  return labels[reason] || reason;
}

export function typeLabel(type, labels = {}) {
  if (!type) return WILDCARD_TYPE_LABEL;
  return labels[type] || type;
}

// Normalise a rule's blank reason/type to null (the "any" wildcard), so the JS
// comparisons match the server's coalesce semantics whether a row arrives with
// null or "".
function wildcard(value) {
  return value === undefined || value === null || value === "" ? null : value;
}

// The applicable ENABLED rules that co-govern content of a given (reason, type):
// reason is R or null ("any"), type is T or null. Mirrors
// Admin::ModerationDecision#candidate_rules.
export function applicableRules(rules, reason, type) {
  const r = wildcard(reason);
  const t = wildcard(type);
  return rules
    .filter((rule) => rule.enabled)
    .filter((rule) => wildcard(rule.reason) === null || wildcard(rule.reason) === r)
    .filter((rule) => wildcard(rule.reportable_type) === null || wildcard(rule.reportable_type) === t);
}

// Resolve the engine's outcome for one concrete scenario: given how many reports are
// for the edited reason and how many are for other reasons, which enabled rule wins?
// Faithful to Admin::ModerationDecision#decide — an any-reason rule counts the TOTAL
// (thisReason + other), a reason-specific rule counts only thisReason; the most-severe
// rule whose count meets its threshold wins. Returns the winning rule's descriptor,
// or null when no rule fires.
export function resolveScenario(rules, reason, type, thisReasonReports, otherReports, reasonLabels = {}) {
  const total = thisReasonReports + otherReports;
  const fired = applicableRules(rules, reason, type).filter((rule) => {
    const count = wildcard(rule.reason) === null ? total : thisReasonReports;
    return count >= rule.threshold;
  });
  if (fired.length === 0) return null;

  const winner = fired.reduce((best, rule) =>
    ACTION_SEVERITY[rule.action] > ACTION_SEVERITY[best.action] ? rule : best,
  );
  return {
    action: winner.action,
    threshold: winner.threshold,
    countsAnyReason: wildcard(winner.reason) === null,
    scopeLabel: wildcard(winner.reason) === null ? "of any reason" : `of ${reasonLabel(winner.reason, reasonLabels)}`,
  };
}

// default / edited / custom provenance, by comparing to the shipped seed shape
// (passed from the server so the classification can't drift from seed_defaults!).
export function classifyProvenance(rule, seededDefaults = []) {
  const seed = seededDefaults.find(
    (s) =>
      wildcard(s.reason) === wildcard(rule.reason) &&
      wildcard(s.reportable_type) === wildcard(rule.reportable_type) &&
      s.action === rule.action,
  );
  if (!seed) return "custom";
  if (rule.threshold === seed.threshold && rule.enabled) return "default";
  return "edited";
}

// Guardrail helpers — flag the two dangerous edit directions so the UI can
// confirm before saving. severeReasons is the set of high-severity reason keys.
//
// Over-hide: a hide rule on a broad scope (any reason) or a non-severe reason at a
// low distinct-reporter threshold — one or two reports hiding anyone's content.
export function isAggressiveHide(rule, severeReasons = [], lowThreshold = 2) {
  if (rule.action !== "hide_for_review" || !rule.enabled) return false;
  if (rule.threshold > lowThreshold) return false;
  const reason = wildcard(rule.reason);
  return reason === null || !severeReasons.includes(reason);
}

// Under-protect: disabling or weakening a severe reason's hide rule (dropping the
// safety net on sexual/violence/hate content).
export function weakensSevereRule(before, after, severeReasons = []) {
  if (!before || wildcard(before.reason) === null || !severeReasons.includes(wildcard(before.reason))) return false;
  if (before.action !== "hide_for_review") return false;
  const disabled = before.enabled && !after.enabled;
  const raised = after.threshold > before.threshold;
  const softened = ACTION_SEVERITY[after.action] < ACTION_SEVERITY[before.action];
  return disabled || raised || softened;
}
